import React, {createRef} from "react";
import PropTypes from "prop-types";
import UserDataService from "../common/UserDataService";
import {
    fetchResponseHandler,
    fetchVoidResponseHandler,
    ERROR_CODE_BAD_INPUT,
    ERROR_CODE_SMS_RATE_LIMIT_REACHED, ERROR_CODE_SMS_MAX_LIMIT_REACHED
} from "../../util/FetchUtil";
import ProcessingComponent from "../../util/ProcessingComponent";
import EaseContactInfo from "../../util/EaseContactInfo";
import {PROP_NAME_SEC_Q1, PROP_NAME_SEC_ANS1, PROP_NAME_SEC_Q2, PROP_NAME_SEC_ANS2, PROP_NAME_SEC_Q3, PROP_NAME_SEC_ANS3} from "./input/SecurityQuestionsInputComponent";

class PhoneVerificationComponent extends React.Component {
    constructor(props){
        super(props);
        this.state = {
            countdown: 30, // Initialize countdown timer
            resendDisabled: true, // Initially disable resend button
        };
        this.resend = this.resend.bind(this);
        this.showProcessing = this.showProcessing.bind(this);
        this.hideProcessing = this.hideProcessing.bind(this);
        this.resend = this.resend.bind(this);
        this.setVerificationCode = this.setVerificationCode.bind(this);
        this.verifyPhoneCode = this.verifyPhoneCode.bind(this);
        this.setResent = this.setResent.bind(this);
        this.toggleEaseInfo = this.toggleEaseInfo.bind(this);
        this.isFormValid = this.isFormValid.bind(this);
        this.cancel =  this.cancel.bind(this);
        this.startCountdown= this.startCountdown.bind(this);
        this.securityCodeInput = createRef();
    }

    componentDidMount() {
        const {countryCode, phoneNumber, extension, channel, userDataService, onError, onCancel, securityQnAData} = this.props;
        const qnaList = [];
        if (securityQnAData){
            qnaList.push({question: securityQnAData[PROP_NAME_SEC_Q1], answer: securityQnAData[PROP_NAME_SEC_ANS1]});
            qnaList.push({question: securityQnAData[PROP_NAME_SEC_Q2], answer: securityQnAData[PROP_NAME_SEC_ANS2]});
            qnaList.push({question: securityQnAData[PROP_NAME_SEC_Q3], answer: securityQnAData[PROP_NAME_SEC_ANS3]});
        }
        this.showProcessing();
        const data = {countryCode, phoneNumber, extension, channel, qnaList};
        userDataService.sendMessageToPhone(data).then(
            fetchResponseHandler
        ).then(data => {
            const {verificationCodeSent} = data;
            this.setState(state => ({...state, verificationCodeSent}));
        }).catch(e => {
            onCancel();
            if (e.errorCode === ERROR_CODE_BAD_INPUT ||
                e.errorCode === ERROR_CODE_SMS_RATE_LIMIT_REACHED ||
                e.errorCode === ERROR_CODE_SMS_MAX_LIMIT_REACHED) {
                // cancel out from this screen so user can correct it. Technically, shouldn't
                // even pass the first screen, but just in case.
                onError(e);
            }else{
                //For generic errors request the user to reload page 
                onError("An error occurred while processing your request, please reload this page to continue");
            }
        }).finally(() => {
            this.hideProcessing();
            if (this.securityCodeInput && this.securityCodeInput.current){
                this.securityCodeInput.current.focus();
            }
            this.startCountdown();
        });
    }

    setResent(resent){
        this.setState(state => ({...state, resent}));
    }

    resend(){
        const {userDataService, onError} = this.props;
        this.showProcessing();
        this.setResent(false);
        userDataService.resendPhoneVerificationCode()
            .then(fetchVoidResponseHandler)
            .then(() => this.setResent(true))
            .catch(onError)
            .finally(()=>{
                this.hideProcessing();
                this.startCountdown();
            });
    }

    showProcessing(){
        this.setState(state => ({...state, showProcessing: true}));
    }

    hideProcessing(){
        this.setState(state => ({...state, showProcessing: false}));
    }

    setVerificationCode(e){
        const verificationCode = e.target.value;
        this.setState(state => ({...state, verificationCode, resent: false, invalidCode: false}));
    }

    verifyPhoneCode(e){
        if (e){
            e.preventDefault();
        }
        if (! this.isFormValid()){
            // don't do anything if form is not valid
            return;
        }
        this.showProcessing();
        this.setState(state => ({...state, invalidCode: false, resent: false}));
        const {verificationCode} = this.state;
        const {userDataService, onError, onSuccess} = this.props;
        userDataService.verifyPhoneCode({code: verificationCode}).then(fetchResponseHandler).then(data => {
            if (data.success){
                onSuccess(data.verificationReceipt);
                return;
            }
            this.setState(state => ({...state, invalidCode: true, resent: data.resent}));
        }).catch(onError).finally(this.hideProcessing);
    }

    toggleEaseInfo(e){
        e.preventDefault();
        this.setState(state => ({...state, showEaseInfo: !state.showEaseInfo}));
    }

    isFormValid(){
        const {verificationCode} = this.state;
        return verificationCode && verificationCode.trim().length > 0;
    }

    cancel(){
        this.showProcessing();
        const {onCancel, userDataService} = this.props;
        userDataService.cancelPhoneVerification()
            .then(fetchResponseHandler)
            .then(() => onCancel())
            .catch(e => {
                console.log(e);
                onCancel();
            })
            .finally(this.hideProcessing);
    }

    startCountdown() {
        this.setState({ countdown: 30, resendDisabled: true });
        this.countdownInterval = setInterval(() => {
            this.setState((prevState) => {
                if (prevState.countdown > 1) {
                    return { countdown: prevState.countdown - 1 };
                } else {
                    clearInterval(this.countdownInterval);
                    return { countdown: 0, resendDisabled: false };
                }
            });
        }, 1000);
    }

    componentWillUnmount() {
        clearInterval(this.countdownInterval); // Clear the interval when the component unmounts
    }

    render(){
        const {showProcessing, verificationCodeSent, verificationCode, resent, showEaseInfo, invalidCode,resendDisabled, countdown} = this.state;
        const {onCancel, continueButtonLabel="Continue"} = this.props;
        const submitDisabled = ! this.isFormValid();
        return (
            <div>
                {
                    verificationCodeSent &&
                    <div className="push--down">
                        Enter security code that was sent to your mobile device. If you are
                        unable to receive the security code, please <a href="#" onClick={this.toggleEaseInfo}>call CME Enterprise Application &amp; System Entitlement</a>.
                    </div>
                }
                {
                    ! verificationCodeSent &&
                    <div className="push--down">
                        <div className="push--down">
                            Please <a href="#" onClick={this.toggleEaseInfo}>call CME Enterprise Application &amp; System Entitlement</a> for verification code.
                        </div>
                    </div>
                }
                {
                    showEaseInfo &&
                    <div className="panel--raised push--ends">
                        <div className={"text--right"}><i onClick={this.toggleEaseInfo} className="fa fa-times clickable"></i></div>
                        <div>
                            United States +1 312 456 1560<br/>
                            Europe +44 20 3379 3802<br/>
                            Asia +65 6593 5536<br/><br/>
                        </div>
                        {/*<EaseContactInfo />*/}
                    </div>
                }
                <ul className={"form-fields push--ends"}>
                    <li>
                        <form id="myForm" onSubmit={this.verifyPhoneCode}>
                            <input type="text"
                                   className={"text-input text-input--large responsive max-width--30" + ((resent || invalidCode) ? " has-error" : "")}
                                   maxLength={8}
                                   value={verificationCode}
                                   onChange={this.setVerificationCode}
                                   placeholder="Verification code"
                                   ref={this.securityCodeInput}
                            />
                            {
                                invalidCode &&
                                <div className="error">The verification code you entered does not match our record.</div>
                            }
                            {
                                resent &&
                                <div className="error">The security code has been re-sent. Please check your phone.</div>
                            }
                        </form>
                    </li>
                    {
                        verificationCodeSent &&
                        <li>
                            <button className="btn btn--link"
                                    onClick={this.resend}
                                    disabled={resendDisabled}>
                                <i className="fa fa-refresh"></i>
                                {" "}
                                {resendDisabled // Show either the countdown or the resend text
                                    ? `Resend code in ${countdown} seconds`
                                    : "Resend code"}
                            </button>
                        </li>
                    }
                </ul>
                <div className="text--center push--top">
                    <button className="btn btn--large btn--link" onClick={this.cancel}>Cancel</button>
                    <button
                        type="submit"
                        form="myForm"
                        disabled={submitDisabled}
                        onClick={this.verifyPhoneCode}
                        className="btn btn--large push-half--left"
                    >{continueButtonLabel}</button>
                </div>
                <ProcessingComponent show={showProcessing}/>
            </div>
        );
    }

}

PhoneVerificationComponent.propTypes = {
    countryCode: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string.isRequired,
    extension: PropTypes.string,
    channel: PropTypes.string.isRequired,
    userDataService: PropTypes.instanceOf(UserDataService).isRequired,
    onError: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    continueButtonLabel: PropTypes.string,
    securityQnAData: PropTypes.object
};

export default PhoneVerificationComponent;